import { useState, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';

import Page from '@/components/page/Page';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Bot,
  Sparkles,
  Package,
  ArrowRight,
  Plus,
  Check,
  TrendingUp,
  Warehouse,
  Boxes,
} from 'lucide-react';

import { createBuyingAgent } from '../ai-workflows/buyingAgentsStore';

// ============================================================================
// SANKEY DIAGRAM COMPONENT - Professional Enterprise Style
// ============================================================================

interface SecondaryProduct {
  name: string;
  sku: string;
  demandUnits: number;
  materialId: string; // Which material this product uses
}

interface BillOfMaterialsSankeyProps {
  product: typeof billOfMaterials.product;
  materials: typeof billOfMaterials.rawMaterials;
  suppliers: typeof billOfMaterials.suppliers;
  secondaryProduct?: SecondaryProduct | null;
  bomDetails: { materialName: string; unitsPerProduct: number }[];
  rebateSuggestionAdded?: boolean;
  highlightedMaterialSku?: string | null;
}

function BillOfMaterialsSankey({
  product,
  materials,
  suppliers,
  secondaryProduct,
  bomDetails,
  rebateSuggestionAdded = false,
  highlightedMaterialSku = null,
}: BillOfMaterialsSankeyProps) {
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [hoveredLink, setHoveredLink] = useState<string | null>(null);
  const [showBomExpanded, setShowBomExpanded] = useState(false);
  const [showSecondaryBomExpanded, setShowSecondaryBomExpanded] =
    useState(false);

  // SVG dimensions
  const width = 1000;
  const height = secondaryProduct ? 520 : 420;
  const nodeWidth = 210;
  const baseNodeHeight = 88;
  const bomExpandedHeight = bomDetails.length * 16 + 20; // Height for BOM section
  const productNodeHeight = showBomExpanded
    ? baseNodeHeight + bomExpandedHeight
    : baseNodeHeight;
  const secondaryNodeHeight = showSecondaryBomExpanded
    ? baseNodeHeight + 36
    : baseNodeHeight; // Single BOM item
  const nodeHeight = baseNodeHeight; // For materials and other nodes
  const padding = 30;
  const headerHeight = 28;

  // Supplier-specific prices for materials
  // supplierIndex -> materialId -> price
  const supplierPrices: Record<number, Record<string, number>> = {
    0: { 'rm-1': 46.0 }, // Silicon Valley Materials: Silicon Wafers $46
    2: { 'rm-1': 46.5, 'rm-3': 8.5 }, // Industrial Metals: Silicon Wafers $46.50, Ceramic Substrate $8.50
    3: { 'rm-1': 45.0, 'rm-3': 8.75 }, // MetalWorks Inc: Silicon Wafers $45, Ceramic Substrate $8.75
    4: { 'rm-1': 47.0 }, // Pacific Steel Corp: Silicon Wafers $47
  };

  // Helper to get which materials a supplier sells with their specific prices
  const getMaterialsForSupplier = (supplierIndex: number) => {
    const materialIndices: number[] = [];
    // Silicon Wafers (mi=0) connects to suppliers [0, 2, 3, 4]
    if ([0, 2, 3, 4].includes(supplierIndex)) materialIndices.push(0);
    // Ceramic Substrate (mi=2) connects to suppliers [2, 3]
    if ([2, 3].includes(supplierIndex)) materialIndices.push(2);

    return materialIndices
      .map((mi) => {
        const material = materials[mi];
        if (!material) return null;
        // Get supplier-specific price, fall back to material's avgPrice
        const price =
          supplierPrices[supplierIndex]?.[material.id] ?? material.avgPrice;
        return { ...material, supplierPrice: price };
      })
      .filter(Boolean) as ((typeof materials)[0] & { supplierPrice: number })[];
  };

  // Filter suppliers to only show those for materials with shortfall
  const materialsWithShortfall = materials.filter((m) => m.shortfall > 0);
  const suppliersToShow = suppliers.filter((_, i) => {
    // Show supplier if any material with shortfall uses it
    return materialsWithShortfall.some((m) => {
      const mi = materials.indexOf(m);
      // Silicon Wafers (mi=0) connects to all 4 suppliers: Silicon Valley Materials (0), Industrial Metals (2), MetalWorks Inc (3), Pacific Steel Corp (4)
      // Ceramic Substrate (mi=2) connects to Industrial Metals (2) and MetalWorks Inc (3)
      const supplierIndices =
        mi === 0 ? [0, 2, 3, 4] : mi === 2 ? [2, 3] : [mi % suppliers.length];
      return supplierIndices.includes(i);
    });
  });

  // Calculate node positions - main product stays fixed at top
  const productNode = {
    x: padding,
    y: headerHeight + 5, // Fixed position near top - doesn't move when secondary product is added
  };

  const secondaryProductNode = secondaryProduct
    ? {
        x: padding,
        y: height - nodeHeight - 60, // Near bottom, far from main product
      }
    : null;

  const materialNodes = materials.map((_, i) => ({
    x: width / 2 - nodeWidth / 2,
    y: headerHeight + i * (nodeHeight + 12),
  }));

  // Calculate supplier node heights (always show prices)
  const getSupplierNodeHeight = (supplierId: string) => {
    const originalIndex = suppliers.findIndex((s) => s.id === supplierId);
    const materialCount = getMaterialsForSupplier(originalIndex).length;
    return nodeHeight + 8 + materialCount * 16; // Base + padding + rows
  };

  // Only position suppliers that we're showing (with dynamic heights)
  // Start suppliers higher since there are more of them
  const supplierNodes = suppliersToShow.map((supplier, i) => {
    // Calculate Y position based on heights of all previous nodes
    // Start at headerHeight - 10 to position suppliers higher
    let yPos = headerHeight - 10;
    for (let j = 0; j < i; j++) {
      yPos += getSupplierNodeHeight(suppliersToShow[j].id) + 6;
    }
    return {
      x: width - nodeWidth - padding,
      y: yPos,
      height: getSupplierNodeHeight(supplier.id),
    };
  });

  // Generate smooth curved path for links
  const generatePath = (
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    thickness: number = 16,
  ) => {
    const midX = (x1 + x2) / 2;
    return `
      M ${x1} ${y1 - thickness / 2}
      C ${midX} ${y1 - thickness / 2}, ${midX} ${y2 - thickness / 2}, ${x2} ${y2 - thickness / 2}
      L ${x2} ${y2 + thickness / 2}
      C ${midX} ${y2 + thickness / 2}, ${midX} ${y1 + thickness / 2}, ${x1} ${y1 + thickness / 2}
      Z
    `;
  };

  // Generate a rectangle path with only right corners rounded (left side stays straight for accent bar)
  const roundedRightRect = (
    x: number,
    y: number,
    w: number,
    h: number,
    r: number,
  ) => {
    return `
      M ${x} ${y}
      L ${x + w - r} ${y}
      Q ${x + w} ${y} ${x + w} ${y + r}
      L ${x + w} ${y + h - r}
      Q ${x + w} ${y + h} ${x + w - r} ${y + h}
      L ${x} ${y + h}
      Z
    `;
  };

  // Get status for material: Healthy Stock (buffer), Low Stock (no buffer), Critical (shortfall)
  const getMaterialStatus = (material: (typeof materials)[0]) => {
    if (material.shortfall > material.neededQuantity * 0.3) return 'critical';
    if (material.shortfall > 0) return 'warning';
    // No buffer (stock exactly meets or barely exceeds need) is also risky
    if (material.currentStock <= material.neededQuantity) return 'warning';
    return 'ok';
  };

  return (
    <div className="relative w-full h-full rounded-xl border border-border/50 bg-gradient-to-b from-background to-muted/20 p-3 flex flex-col">
      {/* Header with legend */}
      <div className="flex items-center justify-between mb-2 pb-2 border-b border-border/50">
        <div className="flex items-center gap-6 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-sm bg-violet-600" />
            <span className="text-muted-foreground font-medium">Products</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-sm bg-slate-600" />
            <span className="text-muted-foreground font-medium">
              Raw Materials
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-sm bg-blue-600" />
            <span className="text-muted-foreground font-medium">Suppliers</span>
          </div>
        </div>
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            <span className="text-muted-foreground">Sufficient</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
            <span className="text-muted-foreground">Low Stock</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-rose-500" />
            <span className="text-muted-foreground">Critical</span>
          </div>
        </div>
      </div>

      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full flex-1 mt-4"
        preserveAspectRatio="xMidYMin meet"
      >
        <defs>
          {/* Link gradients - subtle and professional */}
          <linearGradient
            id="linkProductMaterial"
            x1="0%"
            y1="0%"
            x2="100%"
            y2="0%"
          >
            <stop offset="0%" stopColor="#7c3aed" stopOpacity="0.25" />
            <stop offset="50%" stopColor="#64748b" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#64748b" stopOpacity="0.25" />
          </linearGradient>
          <linearGradient
            id="linkMaterialSupplier"
            x1="0%"
            y1="0%"
            x2="100%"
            y2="0%"
          >
            <stop offset="0%" stopColor="#64748b" stopOpacity="0.25" />
            <stop offset="50%" stopColor="#64748b" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#2563eb" stopOpacity="0.25" />
          </linearGradient>
          <linearGradient
            id="linkProductMaterialHover"
            x1="0%"
            y1="0%"
            x2="100%"
            y2="0%"
          >
            <stop offset="0%" stopColor="#7c3aed" stopOpacity="0.5" />
            <stop offset="50%" stopColor="#64748b" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#64748b" stopOpacity="0.5" />
          </linearGradient>
          <linearGradient
            id="linkMaterialSupplierHover"
            x1="0%"
            y1="0%"
            x2="100%"
            y2="0%"
          >
            <stop offset="0%" stopColor="#64748b" stopOpacity="0.5" />
            <stop offset="50%" stopColor="#64748b" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#2563eb" stopOpacity="0.5" />
          </linearGradient>

          {/* Secondary product link gradients - purple to match main product */}
          <linearGradient
            id="linkSecondaryMaterial"
            x1="0%"
            y1="0%"
            x2="100%"
            y2="0%"
          >
            <stop offset="0%" stopColor="#7c3aed" stopOpacity="0.25" />
            <stop offset="50%" stopColor="#64748b" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#64748b" stopOpacity="0.25" />
          </linearGradient>
          <linearGradient
            id="linkSecondaryMaterialHover"
            x1="0%"
            y1="0%"
            x2="100%"
            y2="0%"
          >
            <stop offset="0%" stopColor="#7c3aed" stopOpacity="0.5" />
            <stop offset="50%" stopColor="#64748b" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#64748b" stopOpacity="0.5" />
          </linearGradient>

          {/* Enhanced rebate link gradient - blue tone matching suppliers */}
          <linearGradient
            id="linkMaterialSupplierRebate"
            x1="0%"
            y1="0%"
            x2="100%"
            y2="0%"
          >
            <stop offset="0%" stopColor="#64748b" stopOpacity="0.3" />
            <stop offset="40%" stopColor="#3b82f6" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#2563eb" stopOpacity="0.5" />
          </linearGradient>
          <linearGradient
            id="linkMaterialSupplierRebateHover"
            x1="0%"
            y1="0%"
            x2="100%"
            y2="0%"
          >
            <stop offset="0%" stopColor="#64748b" stopOpacity="0.4" />
            <stop offset="40%" stopColor="#3b82f6" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#2563eb" stopOpacity="0.7" />
          </linearGradient>

          {/* Subtle shadow */}
          <filter id="nodeShadow" x="-10%" y="-10%" width="120%" height="130%">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.08" />
          </filter>
          <filter
            id="nodeShadowHover"
            x="-10%"
            y="-10%"
            width="120%"
            height="130%"
          >
            <feDropShadow dx="0" dy="4" stdDeviation="6" floodOpacity="0.12" />
          </filter>
          {/* Colored shadows for AI suggestion preview highlights */}
          <filter
            id="nodeShadowWarning"
            x="-20%"
            y="-20%"
            width="140%"
            height="150%"
          >
            <feDropShadow
              dx="0"
              dy="0"
              stdDeviation="8"
              floodColor="#d97706"
              floodOpacity="0.4"
            />
          </filter>
          <filter
            id="nodeShadowCritical"
            x="-20%"
            y="-20%"
            width="140%"
            height="150%"
          >
            <feDropShadow
              dx="0"
              dy="0"
              stdDeviation="8"
              floodColor="#dc2626"
              floodOpacity="0.4"
            />
          </filter>
          <filter
            id="nodeShadowHealthy"
            x="-20%"
            y="-20%"
            width="140%"
            height="150%"
          >
            <feDropShadow
              dx="0"
              dy="0"
              stdDeviation="8"
              floodColor="#059669"
              floodOpacity="0.4"
            />
          </filter>
        </defs>

        {/* Links from Product to Materials */}
        {materials.map((material, i) => {
          const linkId = `product-${material.id}`;
          const isHovered =
            hoveredLink === linkId ||
            hoveredNode === 'product' ||
            hoveredNode === material.id;
          const thickness = Math.max(
            6,
            Math.min(18, material.neededQuantity / 280),
          );

          return (
            <path
              key={linkId}
              d={generatePath(
                productNode.x + nodeWidth,
                productNode.y + nodeHeight / 2,
                materialNodes[i].x,
                materialNodes[i].y + nodeHeight / 2,
                thickness,
              )}
              fill={
                isHovered
                  ? 'url(#linkProductMaterialHover)'
                  : 'url(#linkProductMaterial)'
              }
              className="transition-all duration-200"
              onMouseEnter={() => setHoveredLink(linkId)}
              onMouseLeave={() => setHoveredLink(null)}
            />
          );
        })}

        {/* Link from Secondary Product to its Material */}
        {secondaryProduct &&
          secondaryProductNode &&
          (() => {
            const materialIndex = materials.findIndex(
              (m) => m.id === secondaryProduct.materialId,
            );
            if (materialIndex === -1) return null;

            const linkId = `secondary-${secondaryProduct.materialId}`;
            const isHovered =
              hoveredLink === linkId ||
              hoveredNode === 'secondary-product' ||
              hoveredNode === secondaryProduct.materialId;
            const thickness = 10;

            return (
              <path
                key={linkId}
                d={generatePath(
                  secondaryProductNode.x + nodeWidth,
                  secondaryProductNode.y + nodeHeight / 2,
                  materialNodes[materialIndex].x,
                  materialNodes[materialIndex].y + nodeHeight / 2 + 15,
                  thickness,
                )}
                fill={
                  isHovered
                    ? 'url(#linkSecondaryMaterialHover)'
                    : 'url(#linkSecondaryMaterial)'
                }
                className="transition-all duration-200"
                onMouseEnter={() => setHoveredLink(linkId)}
                onMouseLeave={() => setHoveredLink(null)}
              />
            );
          })()}

        {/* Links from Materials to Suppliers - only for materials with shortfall */}
        {materials.flatMap((material, mi) => {
          // Only show supplier links for materials with shortfall
          if (material.shortfall <= 0) return [];

          // Silicon Wafers (mi=0) connects to all 4 suppliers: Silicon Valley Materials (0), Industrial Metals (2), MetalWorks Inc (3), Pacific Steel Corp (4)
          // Ceramic Substrate (mi=2) connects to Industrial Metals (2) and MetalWorks Inc (3)
          const originalSupplierIndices =
            mi === 0
              ? [0, 2, 3, 4]
              : mi === 2
                ? [2, 3]
                : [mi % suppliers.length];

          return originalSupplierIndices
            .map((originalSupplierIndex, linkIdx) => {
              const supplier = suppliers[originalSupplierIndex];
              const displaySupplierIndex = suppliersToShow.findIndex(
                (s) => s.id === supplier.id,
              );
              if (displaySupplierIndex === -1) return null;

              const linkId = `${material.id}-${supplier.id}`;
              const isHovered =
                hoveredLink === linkId ||
                hoveredNode === material.id ||
                hoveredNode === supplier.id;

              // Check if this is the Silicon Wafers -> Silicon Valley Materials link with rebate
              const isRebateEnhancedLink =
                rebateSuggestionAdded &&
                mi === 0 &&
                originalSupplierIndex === 0;
              // Check if this is a non-rebate supplier link for Silicon Wafers when rebate is active
              const isNonRebateSupplierWhenRebateActive =
                rebateSuggestionAdded &&
                mi === 0 &&
                originalSupplierIndex !== 0;

              const baseThickness = Math.max(
                5,
                Math.min(14, material.neededQuantity / 320),
              );

              // Calculate thickness based on rebate state
              let thickness: number;
              if (isRebateEnhancedLink) {
                // Rebate link gets much thicker
                thickness = baseThickness * 1.8;
              } else if (isNonRebateSupplierWhenRebateActive) {
                // Other supplier links get thinner when rebate is active
                thickness = baseThickness * 0.35;
              } else if (originalSupplierIndices.length > 1) {
                // Multiple suppliers without rebate
                thickness = baseThickness * 0.65;
              } else {
                thickness = baseThickness;
              }
              const yOffset =
                originalSupplierIndices.length > 1 ? (linkIdx - 0.5) * 10 : 0;

              // Use enhanced gradient for rebate link
              const getFillGradient = () => {
                if (isRebateEnhancedLink) {
                  return isHovered
                    ? 'url(#linkMaterialSupplierRebateHover)'
                    : 'url(#linkMaterialSupplierRebate)';
                }
                return isHovered
                  ? 'url(#linkMaterialSupplierHover)'
                  : 'url(#linkMaterialSupplier)';
              };

              return (
                <path
                  key={linkId}
                  d={generatePath(
                    materialNodes[mi].x + nodeWidth,
                    materialNodes[mi].y + nodeHeight / 2 + yOffset,
                    supplierNodes[displaySupplierIndex].x,
                    supplierNodes[displaySupplierIndex].y +
                      supplierNodes[displaySupplierIndex].height / 2,
                    thickness,
                  )}
                  fill={getFillGradient()}
                  className="transition-all duration-300"
                  onMouseEnter={() => setHoveredLink(linkId)}
                  onMouseLeave={() => setHoveredLink(null)}
                />
              );
            })
            .filter(Boolean);
        })}

        {/* Column Headers */}
        <text
          x={productNode.x + nodeWidth / 2}
          y={12}
          fontSize="11"
          fill="#64748b"
          fontWeight="600"
          textAnchor="middle"
          style={{ letterSpacing: '0.5px' }}
        >
          PRODUCTS
        </text>
        <text
          x={materialNodes[0].x + nodeWidth / 2}
          y={12}
          fontSize="11"
          fill="#64748b"
          fontWeight="600"
          textAnchor="middle"
          style={{ letterSpacing: '0.5px' }}
        >
          RAW MATERIALS
        </text>
        <text
          x={width - nodeWidth / 2 - padding}
          y={12}
          fontSize="11"
          fill="#64748b"
          fontWeight="600"
          textAnchor="middle"
          style={{ letterSpacing: '0.5px' }}
        >
          SUPPLIERS
        </text>

        {/* Product Node */}
        <g
          className="cursor-pointer"
          onMouseEnter={() => setHoveredNode('product')}
          onMouseLeave={() => setHoveredNode(null)}
        >
          <path
            d={roundedRightRect(
              productNode.x,
              productNode.y,
              nodeWidth,
              productNodeHeight,
              8,
            )}
            fill="white"
            stroke={hoveredNode === 'product' ? '#7c3aed' : '#e2e8f0'}
            strokeWidth={hoveredNode === 'product' ? 2 : 1}
            filter={
              hoveredNode === 'product'
                ? 'url(#nodeShadowHover)'
                : 'url(#nodeShadow)'
            }
            className="transition-all duration-200"
          />
          {/* Left accent bar */}
          <rect
            x={productNode.x}
            y={productNode.y}
            width={4}
            height={productNodeHeight}
            fill="#7c3aed"
          />
          {/* Content */}
          <text
            x={productNode.x + 16}
            y={productNode.y + 24}
            fontSize="12"
            fill="#0f172a"
            fontWeight="600"
          >
            {product.name}
          </text>
          <text
            x={productNode.x + 16}
            y={productNode.y + 40}
            fontSize="10"
            fill="#64748b"
          >
            {product.sku}
          </text>
          {/* See BOM link - click to expand */}
          <text
            x={productNode.x + 16}
            y={productNode.y + 56}
            fontSize="9"
            fill="#7c3aed"
            fontWeight="500"
            className="cursor-pointer"
            style={{ textDecoration: 'underline' }}
            onClick={() => setShowBomExpanded(!showBomExpanded)}
          >
            {showBomExpanded ? 'Hide BOM ↑' : 'See BOM ↓'}
          </text>
          <text
            x={productNode.x + nodeWidth - 12}
            y={productNode.y + 24}
            fontSize="11"
            fill="#7c3aed"
            fontWeight="600"
            textAnchor="end"
          >
            {product.demandUnits.toLocaleString()}
          </text>
          <text
            x={productNode.x + nodeWidth - 12}
            y={productNode.y + 38}
            fontSize="9"
            fill="#94a3b8"
            textAnchor="end"
          >
            units forecasted
          </text>
          <text
            x={productNode.x + nodeWidth - 12}
            y={productNode.y + 50}
            fontSize="8"
            fill="#94a3b8"
            textAnchor="end"
          >
            next 3 months
          </text>

          {/* Expanded BOM Section */}
          {showBomExpanded && (
            <g>
              {/* Divider line */}
              <line
                x1={productNode.x + 12}
                y1={productNode.y + baseNodeHeight - 6}
                x2={productNode.x + nodeWidth - 12}
                y2={productNode.y + baseNodeHeight - 6}
                stroke="#e2e8f0"
                strokeWidth={1}
              />
              <text
                x={productNode.x + 16}
                y={productNode.y + baseNodeHeight + 10}
                fontSize="9"
                fill="#0f172a"
                fontWeight="600"
              >
                BOM (per unit)
              </text>
              {bomDetails.map((bom, i) => (
                <text
                  key={bom.materialName}
                  x={productNode.x + 16}
                  y={productNode.y + baseNodeHeight + 26 + i * 16}
                  fontSize="9"
                  fill="#64748b"
                >
                  • {bom.materialName}: {bom.unitsPerProduct}
                </text>
              ))}
            </g>
          )}
        </g>

        {/* Secondary Product Node */}
        {secondaryProduct && secondaryProductNode && (
          <g
            className="cursor-pointer"
            onMouseEnter={() => setHoveredNode('secondary-product')}
            onMouseLeave={() => setHoveredNode(null)}
          >
            <path
              d={roundedRightRect(
                secondaryProductNode.x,
                secondaryProductNode.y,
                nodeWidth,
                secondaryNodeHeight,
                8,
              )}
              fill="white"
              stroke={
                hoveredNode === 'secondary-product' ? '#7c3aed' : '#e2e8f0'
              }
              strokeWidth={hoveredNode === 'secondary-product' ? 2 : 1}
              filter={
                hoveredNode === 'secondary-product'
                  ? 'url(#nodeShadowHover)'
                  : 'url(#nodeShadow)'
              }
              className="transition-all duration-200"
            />
            {/* Left accent bar - purple to match main product */}
            <rect
              x={secondaryProductNode.x}
              y={secondaryProductNode.y}
              width={4}
              height={secondaryNodeHeight}
              fill="#7c3aed"
            />
            {/* Content */}
            <text
              x={secondaryProductNode.x + 16}
              y={secondaryProductNode.y + 24}
              fontSize="12"
              fill="#0f172a"
              fontWeight="600"
            >
              {secondaryProduct.name.length > 18
                ? secondaryProduct.name.slice(0, 18) + '...'
                : secondaryProduct.name}
            </text>
            <text
              x={secondaryProductNode.x + 16}
              y={secondaryProductNode.y + 40}
              fontSize="10"
              fill="#64748b"
            >
              {secondaryProduct.sku}
            </text>
            {/* See BOM link - click to expand */}
            <text
              x={secondaryProductNode.x + 16}
              y={secondaryProductNode.y + 56}
              fontSize="9"
              fill="#7c3aed"
              fontWeight="500"
              className="cursor-pointer"
              style={{ textDecoration: 'underline' }}
              onClick={() =>
                setShowSecondaryBomExpanded(!showSecondaryBomExpanded)
              }
            >
              {showSecondaryBomExpanded ? 'Hide BOM ↑' : 'See BOM ↓'}
            </text>
            <text
              x={secondaryProductNode.x + nodeWidth - 12}
              y={secondaryProductNode.y + 24}
              fontSize="11"
              fill="#7c3aed"
              fontWeight="600"
              textAnchor="end"
            >
              {secondaryProduct.demandUnits.toLocaleString()}
            </text>
            <text
              x={secondaryProductNode.x + nodeWidth - 12}
              y={secondaryProductNode.y + 38}
              fontSize="9"
              fill="#94a3b8"
              textAnchor="end"
            >
              units forecasted
            </text>
            <text
              x={secondaryProductNode.x + nodeWidth - 12}
              y={secondaryProductNode.y + 50}
              fontSize="8"
              fill="#94a3b8"
              textAnchor="end"
            >
              next 3 months
            </text>

            {/* Expanded BOM Section */}
            {showSecondaryBomExpanded && (
              <g>
                {/* Divider line */}
                <line
                  x1={secondaryProductNode.x + 12}
                  y1={secondaryProductNode.y + baseNodeHeight - 6}
                  x2={secondaryProductNode.x + nodeWidth - 12}
                  y2={secondaryProductNode.y + baseNodeHeight - 6}
                  stroke="#e2e8f0"
                  strokeWidth={1}
                />
                <text
                  x={secondaryProductNode.x + 16}
                  y={secondaryProductNode.y + baseNodeHeight + 10}
                  fontSize="9"
                  fill="#0f172a"
                  fontWeight="600"
                >
                  BOM (per unit)
                </text>
                <text
                  x={secondaryProductNode.x + 16}
                  y={secondaryProductNode.y + baseNodeHeight + 26}
                  fontSize="9"
                  fill="#64748b"
                >
                  • Silicon Wafers (300mm): 0.5
                </text>
              </g>
            )}
          </g>
        )}

        {/* Material Nodes */}
        {materials.map((material, i) => {
          const status = getMaterialStatus(material);
          const accentColor =
            status === 'critical'
              ? '#dc2626'
              : status === 'warning'
                ? '#d97706'
                : '#059669';
          const isHovered = hoveredNode === material.id;
          const isHighlighted = highlightedMaterialSku === material.sku;

          // Determine the shadow filter based on state
          const getShadowFilter = () => {
            if (isHighlighted) {
              // Use colored shadow for AI suggestion preview
              if (status === 'critical') return 'url(#nodeShadowCritical)';
              if (status === 'warning') return 'url(#nodeShadowWarning)';
              return 'url(#nodeShadowHealthy)';
            }
            if (isHovered) return 'url(#nodeShadowHover)';
            return 'url(#nodeShadow)';
          };

          return (
            <g
              key={material.id}
              className="cursor-pointer"
              onMouseEnter={() => setHoveredNode(material.id)}
              onMouseLeave={() => setHoveredNode(null)}
            >
              <path
                d={roundedRightRect(
                  materialNodes[i].x,
                  materialNodes[i].y,
                  nodeWidth,
                  nodeHeight,
                  8,
                )}
                fill="white"
                stroke={isHovered ? accentColor : '#e2e8f0'}
                strokeWidth={isHovered ? 2 : 1}
                filter={getShadowFilter()}
                className="transition-all duration-200"
              />
              {/* Left accent bar */}
              <rect
                x={materialNodes[i].x}
                y={materialNodes[i].y}
                width={4}
                height={nodeHeight}
                fill={accentColor}
              />
              {/* Content */}
              <text
                x={materialNodes[i].x + 16}
                y={materialNodes[i].y + 24}
                fontSize="11"
                fill="#0f172a"
                fontWeight="600"
              >
                {material.name.length > 24
                  ? material.name.slice(0, 24) + '...'
                  : material.name}
              </text>
              {/* Prices - Avg and Best */}
              <text
                x={materialNodes[i].x + nodeWidth - 12}
                y={materialNodes[i].y + 26}
                fontSize="8"
                fill="#64748b"
                textAnchor="end"
              >
                Avg: ${material.avgPrice.toFixed(2)}
              </text>
              <text
                x={materialNodes[i].x + nodeWidth - 12}
                y={materialNodes[i].y + 38}
                fontSize="8"
                fill="#64748b"
                textAnchor="end"
              >
                Best: ${material.bestPrice.toFixed(2)}
              </text>
              {/* Needed quantity - prominent */}
              <text
                x={materialNodes[i].x + 16}
                y={materialNodes[i].y + 44}
                fontSize="10"
                fill="#0f172a"
                fontWeight="500"
              >
                Needed:{' '}
                <tspan fontWeight="700" fill="#7c3aed">
                  {material.neededQuantity.toLocaleString()}
                </tspan>
              </text>
              {/* Current stock */}
              <text
                x={materialNodes[i].x + 16}
                y={materialNodes[i].y + 60}
                fontSize="10"
                fill="#64748b"
              >
                In Stock:{' '}
                <tspan
                  fontWeight="600"
                  fill={material.shortfall > 0 ? accentColor : '#059669'}
                >
                  {material.currentStock.toLocaleString()}
                </tspan>
              </text>
              {/* Shortfall badge */}
              {material.shortfall > 0 && (
                <g>
                  <rect
                    x={materialNodes[i].x + nodeWidth - 70}
                    y={materialNodes[i].y + 48}
                    width={58}
                    height={22}
                    rx="4"
                    fill={status === 'critical' ? '#fef2f2' : '#fffbeb'}
                  />
                  <text
                    x={materialNodes[i].x + nodeWidth - 41}
                    y={materialNodes[i].y + 63}
                    fontSize="10"
                    fill={accentColor}
                    fontWeight="600"
                    textAnchor="middle"
                  >
                    -{material.shortfall.toLocaleString()}
                  </text>
                </g>
              )}
            </g>
          );
        })}

        {/* Supplier Nodes - only show suppliers for materials with shortfall */}
        {suppliersToShow.map((supplier, i) => {
          const isHovered = hoveredNode === supplier.id;
          // Get the original index to find which materials this supplier sells
          const originalIndex = suppliers.findIndex(
            (s) => s.id === supplier.id,
          );
          const supplierMaterials = getMaterialsForSupplier(originalIndex);
          const currentHeight = supplierNodes[i].height;

          return (
            <g
              key={supplier.id}
              className="cursor-pointer"
              onMouseEnter={() => setHoveredNode(supplier.id)}
              onMouseLeave={() => setHoveredNode(null)}
            >
              <path
                d={roundedRightRect(
                  supplierNodes[i].x,
                  supplierNodes[i].y,
                  nodeWidth,
                  currentHeight,
                  8,
                )}
                fill="white"
                stroke={isHovered ? '#2563eb' : '#e2e8f0'}
                strokeWidth={isHovered ? 2 : 1}
                filter={
                  isHovered ? 'url(#nodeShadowHover)' : 'url(#nodeShadow)'
                }
                className="transition-all duration-200"
              />
              {/* Left accent bar */}
              <rect
                x={supplierNodes[i].x}
                y={supplierNodes[i].y}
                width={4}
                height={currentHeight}
                fill="#2563eb"
              />
              {/* Content */}
              <text
                x={supplierNodes[i].x + 16}
                y={supplierNodes[i].y + 24}
                fontSize="11"
                fill="#0f172a"
                fontWeight="600"
              >
                {supplier.name.length > 20
                  ? supplier.name.slice(0, 20) + '...'
                  : supplier.name}
              </text>
              {/* Location */}
              <text
                x={supplierNodes[i].x + 16}
                y={supplierNodes[i].y + 42}
                fontSize="9"
                fill="#64748b"
              >
                📍 {supplier.location}
              </text>
              {/* Lead time */}
              <text
                x={supplierNodes[i].x + 16}
                y={supplierNodes[i].y + 56}
                fontSize="9"
                fill="#64748b"
              >
                ⏱ {supplier.leadTime} days lead time
              </text>
              {/* Rating badge */}
              <g>
                <rect
                  x={supplierNodes[i].x + nodeWidth - 44}
                  y={supplierNodes[i].y + 10}
                  width={32}
                  height={18}
                  rx="4"
                  fill="#eff6ff"
                />
                <text
                  x={supplierNodes[i].x + nodeWidth - 28}
                  y={supplierNodes[i].y + 23}
                  fontSize="10"
                  fill="#2563eb"
                  fontWeight="600"
                  textAnchor="middle"
                >
                  ★ {supplier.rating}
                </text>
              </g>

              {/* Prices section */}
              <g>
                {/* Divider line */}
                <line
                  x1={supplierNodes[i].x + 12}
                  y1={supplierNodes[i].y + 68}
                  x2={supplierNodes[i].x + nodeWidth - 12}
                  y2={supplierNodes[i].y + 68}
                  stroke="#e2e8f0"
                  strokeWidth={1}
                />
                {/* Product prices */}
                {supplierMaterials.map((material, mi) => (
                  <g key={material.id}>
                    <text
                      x={supplierNodes[i].x + 16}
                      y={supplierNodes[i].y + 82 + mi * 16}
                      fontSize="9"
                      fill="#64748b"
                    >
                      {material.name}
                    </text>
                    <text
                      x={supplierNodes[i].x + nodeWidth - 16}
                      y={supplierNodes[i].y + 82 + mi * 16}
                      fontSize="10"
                      fill="#0f172a"
                      fontWeight="600"
                      textAnchor="end"
                    >
                      ${material.supplierPrice.toFixed(2)}
                    </text>
                  </g>
                ))}
              </g>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

interface OrderItem {
  id: string;
  name: string;
  sku: string;
  quantity: number;
  unitPrice: number;
  category: string;
  reason: string;
}

interface AISuggestion {
  id: string;
  type: 'rebate' | 'bundle';
  title: string;
  bulletPoints: string[];
  savings: string;
  product: {
    id: string;
    name: string;
    sku: string;
    quantity: number;
    unitPrice: number;
  };
  added: boolean;
}

// Bill of Materials data
const billOfMaterials = {
  product: {
    name: 'Advanced Microchip X1',
    sku: 'MCX1-2026',
    demandUnits: 10000,
  },
  rawMaterials: [
    {
      id: 'rm-1',
      name: 'Silicon Wafers (300mm)',
      sku: 'SIL-300',
      requiredPerUnit: 0.5,
      currentStock: 2500,
      neededQuantity: 5000,
      shortfall: 2500,
      supplier: 'Silicon Valley Materials',
      avgPrice: 46.0,
      bestPrice: 45.0, // MetalWorks Inc
    },
    {
      id: 'rm-2',
      name: 'Gold Bonding Wire',
      sku: 'GBW-001',
      requiredPerUnit: 0.02,
      currentStock: 250,
      neededQuantity: 200,
      shortfall: 0,
      supplier: 'Precious Metals Co',
      avgPrice: 128.5,
      bestPrice: 125.0,
    },
    {
      id: 'rm-3',
      name: 'Ceramic Substrate',
      sku: 'CER-SUB',
      requiredPerUnit: 1,
      currentStock: 10000,
      neededQuantity: 10000,
      shortfall: 0,
      supplier: 'Industrial Metals',
      avgPrice: 8.75,
      bestPrice: 8.5, // Industrial Metals
    },
  ],
  suppliers: [
    {
      id: 'sup-1',
      name: 'Silicon Valley Materials',
      rating: 4.8,
      leadTime: 14,
      location: 'San Jose, CA',
      contact: 'orders@svmaterials.com',
    },
    {
      id: 'sup-2',
      name: 'Precious Metals Co',
      rating: 4.5,
      leadTime: 7,
      location: 'Denver, CO',
      contact: 'sales@preciousmetals.com',
    },
    {
      id: 'sup-3',
      name: 'Industrial Metals',
      rating: 4.7,
      leadTime: 10,
      location: 'Austin, TX',
      contact: 'quotes@industrialmetals.com',
    },
    {
      id: 'sup-4',
      name: 'MetalWorks Inc',
      rating: 4.6,
      leadTime: 12,
      location: 'Chicago, IL',
      contact: 'orders@metalworks.com',
    },
    {
      id: 'sup-5',
      name: 'Pacific Steel Corp',
      rating: 4.4,
      leadTime: 8,
      location: 'Seattle, WA',
      contact: 'sales@pacificsteel.com',
    },
  ],
};

export function ProcurementPlanPage() {
  const navigate = useNavigate();

  // Track which suggestion is being hovered for preview
  const [hoveredSuggestionId, setHoveredSuggestionId] = useState<string | null>(
    null,
  );

  // Base order items (from demand forecast)
  const [orderItems, setOrderItems] = useState<OrderItem[]>([
    {
      id: 'item-1',
      name: 'Silicon Wafers (300mm)',
      sku: 'SIL-300',
      quantity: 2500,
      unitPrice: 45,
      category: 'Raw Materials',
      reason: 'Meet microchip production demand',
    },
  ]);

  // AI Suggestions
  const [suggestions, setSuggestions] = useState<AISuggestion[]>([
    {
      id: 'sug-1',
      type: 'rebate',
      title: 'Growth Rebate Opportunity',
      bulletPoints: [
        'Order 100+ additional units',
        '15% growth rebate with Silicon Valley Materials',
        'Estimated savings: $1,687',
      ],
      savings: '$1,687',
      product: {
        id: 'add-1',
        name: 'Silicon Wafers (300mm)',
        sku: 'SIL-300',
        quantity: 100,
        unitPrice: 45,
      },
      added: false,
    },
    {
      id: 'sug-2',
      type: 'bundle',
      title: 'Bundle Opportunity Detected',
      bulletPoints: [
        'Power Module PM-200 also needs Silicon Wafers',
        'Bundle now for 8% bulk discount',
        'Warehouse capacity verified',
      ],
      savings: '$3,200',
      product: {
        id: 'add-2',
        name: 'Silicon Wafers (300mm)',
        sku: 'SIL-300',
        quantity: 750,
        unitPrice: 45,
      },
      added: false,
    },
    {
      id: 'sug-3',
      type: 'bundle',
      title: 'Proactive Stock Recommendation',
      bulletPoints: [
        'Ceramic Substrate likely out of stock by the end of the quarter',
        'This item typically delayed 2-3 weeks from suppliers',
        'We recommend buying ahead to avoid production delays',
      ],
      savings: '$420',
      product: {
        id: 'add-3',
        name: 'Ceramic Substrate',
        sku: 'CER-SUB',
        quantity: 1200,
        unitPrice: 8.75,
      },
      added: false,
    },
  ]);

  // Calculate effective order items (base + added suggestions merged by SKU)
  // Also includes preview items when hovering over a suggestion
  const effectiveOrderItems = useMemo(() => {
    const addedSuggestions = suggestions.filter((s) => s.added);
    // Include hovered suggestion for preview (if not already added)
    const hoveredSuggestion = suggestions.find(
      (s) => s.id === hoveredSuggestionId && !s.added,
    );

    // Start with existing order items, merging any matching suggestions
    const existingItems = orderItems.map((item) => {
      // Find suggestions that match this item's SKU
      const matchingSuggestions = addedSuggestions.filter(
        (s) => s.product.sku === item.sku,
      );

      // Check if hovered suggestion matches this item
      const isHoveredMatch =
        hoveredSuggestion && hoveredSuggestion.product.sku === item.sku;
      const previewQuantity = isHoveredMatch
        ? hoveredSuggestion.product.quantity
        : 0;
      const previewSavings = isHoveredMatch
        ? parseFloat(hoveredSuggestion.savings.replace(/[$,]/g, ''))
        : 0;

      // Calculate additional quantity from suggestions
      const additionalQuantity = matchingSuggestions.reduce(
        (sum, s) => sum + s.product.quantity,
        0,
      );

      // Calculate savings from matching suggestions
      const itemSavings = matchingSuggestions.reduce(
        (sum, s) => sum + parseFloat(s.savings.replace(/[$,]/g, '')),
        0,
      );

      return {
        ...item,
        quantity: item.quantity + additionalQuantity + previewQuantity,
        baseQuantity: item.quantity,
        additionalQuantity: additionalQuantity + previewQuantity,
        savings: itemSavings + previewSavings,
        hasSuggestions: matchingSuggestions.length > 0 || isHoveredMatch,
        isPreview: isHoveredMatch && matchingSuggestions.length === 0,
      };
    });

    // Find suggestions that don't match any existing order item (new items)
    const existingSkus = orderItems.map((item) => item.sku);
    const newItemSuggestions = addedSuggestions.filter(
      (s) => !existingSkus.includes(s.product.sku),
    );

    // Group new suggestions by SKU and create new order items
    const newItemsBySku = new Map<string, typeof addedSuggestions>();
    for (const s of newItemSuggestions) {
      const existing = newItemsBySku.get(s.product.sku) || [];
      newItemsBySku.set(s.product.sku, [...existing, s]);
    }

    const newItems = Array.from(newItemsBySku.entries()).map(
      ([sku, skuSuggestions]) => {
        const firstSuggestion = skuSuggestions[0];
        const totalQuantity = skuSuggestions.reduce(
          (sum, s) => sum + s.product.quantity,
          0,
        );
        const totalSavings = skuSuggestions.reduce(
          (sum, s) => sum + parseFloat(s.savings.replace(/[$,]/g, '')),
          0,
        );

        return {
          id: `new-${sku}`,
          name: firstSuggestion.product.name,
          sku,
          quantity: totalQuantity,
          unitPrice: firstSuggestion.product.unitPrice,
          category: 'Raw Materials',
          reason: 'Added from AI suggestion',
          baseQuantity: 0,
          additionalQuantity: totalQuantity,
          savings: totalSavings,
          hasSuggestions: true,
          isPreview: false,
        };
      },
    );

    // Add preview item for hovered suggestion that would create a new item
    const previewItems: typeof newItems = [];
    if (
      hoveredSuggestion &&
      !existingSkus.includes(hoveredSuggestion.product.sku) &&
      !newItemsBySku.has(hoveredSuggestion.product.sku)
    ) {
      previewItems.push({
        id: `preview-${hoveredSuggestion.product.sku}`,
        name: hoveredSuggestion.product.name,
        sku: hoveredSuggestion.product.sku,
        quantity: hoveredSuggestion.product.quantity,
        unitPrice: hoveredSuggestion.product.unitPrice,
        category: 'Raw Materials',
        reason: 'Preview from AI suggestion',
        baseQuantity: 0,
        additionalQuantity: hoveredSuggestion.product.quantity,
        savings: parseFloat(hoveredSuggestion.savings.replace(/[$,]/g, '')),
        hasSuggestions: true,
        isPreview: true,
      });
    }

    return [...existingItems, ...newItems, ...previewItems];
  }, [orderItems, suggestions, hoveredSuggestionId]);

  // Calculate totals
  const orderSummary = useMemo(() => {
    const subtotal = effectiveOrderItems.reduce(
      (sum, item) => sum + item.quantity * item.unitPrice,
      0,
    );
    const estimatedSavings = effectiveOrderItems.reduce(
      (sum, item) => sum + item.savings,
      0,
    );

    return {
      subtotal,
      totalItems: effectiveOrderItems.length,
      estimatedSavings,
      grandTotal: subtotal - estimatedSavings,
    };
  }, [effectiveOrderItems]);

  // Calculate secondary product for Bill of Materials visualization
  // Show when the Silicon Wafers bundle suggestion is added OR being hovered (for preview)
  const secondaryProductForBOM = useMemo(() => {
    const siliconWafersBundleSuggestion = suggestions.find(
      (s) => s.type === 'bundle' && s.product.sku === 'SIL-300',
    );
    if (!siliconWafersBundleSuggestion) return null;

    // Show if added OR if being hovered for preview
    const isAddedOrHovered =
      siliconWafersBundleSuggestion.added ||
      hoveredSuggestionId === siliconWafersBundleSuggestion.id;

    if (!isAddedOrHovered) return null;

    // The bundle is for Silicon Wafers, which is used by "Power Module PM-200"
    return {
      name: 'Power Module PM-200',
      sku: 'PM-200',
      demandUnits: 1500, // Fixed forecast units for the power module
      materialId: 'rm-1', // Silicon Wafers
    };
  }, [suggestions, hoveredSuggestionId]);

  // Calculate effective materials with adjusted quantities based on suggestions
  const effectiveMaterials = useMemo(() => {
    const bundleSuggestion = suggestions.find(
      (s) => s.type === 'bundle' && s.added,
    );

    return billOfMaterials.rawMaterials.map((material) => {
      // If bundle is added and this is Silicon Wafers, increase needed quantity
      if (bundleSuggestion && material.id === 'rm-1') {
        const additionalNeeded = bundleSuggestion.product.quantity;
        return {
          ...material,
          neededQuantity: material.neededQuantity + additionalNeeded,
          shortfall: material.shortfall + additionalNeeded,
        };
      }
      return material;
    });
  }, [suggestions]);

  const handleAddSuggestion = (suggestionId: string) => {
    setSuggestions((prev) =>
      prev.map((s) => (s.id === suggestionId ? { ...s, added: !s.added } : s)),
    );
  };

  const handleTriggerAgent = () => {
    // Create items list for the agent using effective quantities
    const allItems = effectiveOrderItems.map((item) => item.name);

    // Create a new agent
    const agentId = createBuyingAgent({
      product: allItems.join(', '),
      productCategory: 'Raw Materials',
      quantity: effectiveOrderItems[0].quantity,
      estimatedValue: orderSummary.grandTotal,
      leadTimeDays: 14,
      sku: effectiveOrderItems[0].sku,
    });

    // Navigate to the agent details page
    navigate({
      to: '/procurement-demo/ai-workflows/$agentId',
      params: { agentId },
    });
  };

  return (
    <Page
      title="Plan Procurement"
      subtitle="Review AI recommendations before triggering the procurement agent"
    >
      {/* Main Layout: Bill of Materials (left) + Right Column (AI Suggestions + Procurement Order) */}
      <div className="grid grid-cols-4 gap-6 min-h-[calc(100vh-180px)]">
        {/* Bill of Materials Sankey Diagram - Left Side */}
        <div className="col-span-3 flex flex-col h-full">
          <div className="flex items-center gap-2 px-1 mb-2">
            <Boxes className="h-5 w-5 text-violet-600" />
            <h2 className="text-lg font-semibold">
              Microchip X1: 3-Month Demand
            </h2>
          </div>
          <div className="flex-1 min-h-0">
            <BillOfMaterialsSankey
              product={billOfMaterials.product}
              materials={effectiveMaterials}
              suppliers={billOfMaterials.suppliers}
              secondaryProduct={secondaryProductForBOM}
              bomDetails={billOfMaterials.rawMaterials.map((m) => ({
                materialName: m.name,
                unitsPerProduct: m.requiredPerUnit,
              }))}
              rebateSuggestionAdded={
                suggestions.find((s) => s.type === 'rebate')?.added ?? false
              }
              highlightedMaterialSku={
                hoveredSuggestionId
                  ? suggestions.find((s) => s.id === hoveredSuggestionId)
                      ?.product.sku
                  : null
              }
            />
          </div>
        </div>

        {/* Right Column: AI Suggestions + Procurement Order */}
        <div className="space-y-4 mt-8">
          {/* AI Suggestions */}
          <Card className="border-violet-200 bg-gradient-to-br from-violet-50 to-blue-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-violet-500" />
                AI Suggestions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-0">
              {suggestions.map((suggestion) => (
                <div
                  key={suggestion.id}
                  className={`p-3 rounded-lg border-2 transition-all cursor-pointer ${
                    suggestion.added
                      ? 'bg-emerald-50 border-emerald-300'
                      : hoveredSuggestionId === suggestion.id
                        ? 'bg-violet-50 border-violet-400'
                        : 'bg-white border-dashed border-gray-300 hover:border-violet-300'
                  }`}
                  onMouseEnter={() => setHoveredSuggestionId(suggestion.id)}
                  onMouseLeave={() => setHoveredSuggestionId(null)}
                >
                  <div className="flex items-start gap-2 mb-2">
                    {suggestion.type === 'rebate' ? (
                      <TrendingUp className="h-4 w-4 text-violet-600 flex-shrink-0 mt-0.5" />
                    ) : (
                      <Warehouse className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm leading-tight">
                        {suggestion.title}
                      </div>
                      <Badge
                        variant="outline"
                        className="mt-1 bg-emerald-50 text-emerald-700 border-emerald-200 text-xs px-1.5 py-0"
                      >
                        Save {suggestion.savings}
                      </Badge>
                    </div>
                  </div>
                  <ul className="space-y-1 mb-2.5">
                    {suggestion.bulletPoints.map((point, idx) => (
                      <li
                        key={idx}
                        className="text-xs text-muted-foreground flex items-start gap-1.5"
                      >
                        <span className="text-violet-400 mt-0.5">•</span>
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    variant={suggestion.added ? 'secondary' : 'default'}
                    size="sm"
                    className={`w-full h-8 text-xs ${
                      suggestion.added
                        ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                        : ''
                    }`}
                    onClick={() => handleAddSuggestion(suggestion.id)}
                  >
                    {suggestion.added ? (
                      <>
                        <Check className="h-3 w-3 mr-1" />
                        Added
                      </>
                    ) : (
                      <>
                        <Plus className="h-3 w-3 mr-1" />
                        Add to Order
                      </>
                    )}
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Procurement Order */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Package className="h-4 w-4 text-blue-600" />
                Order Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 pt-0">
              {/* Order Items List */}
              <div className="space-y-1.5">
                {effectiveOrderItems.map((item) => (
                  <div
                    key={item.id}
                    className={`flex items-center justify-between p-2 rounded border text-xs transition-all ${
                      item.isPreview
                        ? 'border-dashed border-violet-300 bg-violet-50/50 opacity-80'
                        : item.hasSuggestions
                          ? 'border-emerald-200 bg-emerald-50'
                          : 'border-border'
                    }`}
                  >
                    <div className="min-w-0">
                      <div className="font-medium truncate flex items-center gap-1">
                        {item.name}
                        {item.isPreview ? (
                          <span className="text-[10px] text-violet-500 font-normal">
                            (preview)
                          </span>
                        ) : (
                          item.hasSuggestions && (
                            <Sparkles className="h-3 w-3 text-emerald-600 flex-shrink-0" />
                          )
                        )}
                      </div>
                      <div className="text-muted-foreground">{item.sku}</div>
                      {item.hasSuggestions && !item.isPreview && (
                        <div className="text-emerald-600 text-[10px]">
                          +{item.additionalQuantity.toLocaleString()} from AI
                          suggestions
                        </div>
                      )}
                      {item.isPreview && (
                        <div className="text-violet-500 text-[10px]">
                          +{item.additionalQuantity.toLocaleString()} if added
                        </div>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0 ml-2">
                      <div className="font-semibold">
                        {item.quantity.toLocaleString()}
                      </div>
                      <div className="text-muted-foreground">
                        ${(item.quantity * item.unitPrice).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="pt-2 border-t space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>${orderSummary.subtotal.toLocaleString()}</span>
                </div>
                {orderSummary.estimatedSavings > 0 && (
                  <div className="flex justify-between text-emerald-600">
                    <span>Savings</span>
                    <span>
                      -${orderSummary.estimatedSavings.toLocaleString()}
                    </span>
                  </div>
                )}
                <div className="flex justify-between font-semibold text-sm pt-1 border-t">
                  <span>Estimated Total</span>
                  <span>${orderSummary.grandTotal.toLocaleString()}</span>
                </div>
              </div>

              {/* Trigger Button */}
              <Button
                className="w-full bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-700 hover:to-blue-700 mt-3"
                onClick={handleTriggerAgent}
              >
                <Bot className="h-4 w-4 mr-1.5" />
                Trigger Agent
                <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </Page>
  );
}
